import { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import _ from 'lodash'
import CachedIcon from '@mui/icons-material/Cached'

import SaveIcon from '@mui/icons-material/Save'
import ClearIcon from '@mui/icons-material/Clear'
import AddIcon from '@mui/icons-material/Add'
import DoneIcon from '@mui/icons-material/Done'
import DeleteIcon from '@mui/icons-material/Delete'
import LowPriorityIcon from '@mui/icons-material/LowPriority'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { makeStyles } from '@mui/styles'
import { Accordion, AccordionSummary, AccordionDetails, Chip, Typography, Stack, Button, Grid, Box, Snackbar, Alert, Card, Tab } from '@mui/material'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { constants } from '../../Constants/constants'
import { getOne, newPut, newPatch } from '../../helper/baseFetch_v2'
import { setAppVersion, setAppVersionValue } from '../../actions/appVersionAction'
import { getString, permissionsCheck } from '../../helper/util'
import { EditCreateDisplayV2 } from '../../Component/EditCreateDisplay/EditCreateDisplayV2'
import { TempTextInput } from '../../Component/Input/TempTextInput'
import { TempAutoSelectInput } from '../../Component/Input/TempAutoSelectInput'
import { TempCheckBox } from '../../Component/Input/TempCheckBox'
import CheckboxGroupInput from '../../Component/Input/CheckboxGroupInput'
import { TempBtn } from '../../Component/Input/TempBtn'
import { UploadImage } from '../../Component/UploadImage/UploadImage'
import Iconify from '../../Component/common/iconify/Iconify'
import { ICONIFY } from '../../Constants/iconify'
import { PICTURE_SIZE, UPLOAD_TYPE, IMAGE_TYPE } from '../../Constants/constants'
import { chipRender } from './constants'
import { RestaurantList } from '../Restaurant/RestaurantList'
import { GlobalDialog } from '../../Component/GlobalDialog'
import { validateForm } from '../../validations/appVersionValidation'
import { formFields } from './AppVersionFormFields'
import SharedDialog from '../../Component/common/dialog/SharedDialog'
import StatusLabel from '../../Component/_v2/label/StatusChip'

import variables from '../../styles/globalVariables.scss'

import '../../styles/merchantUser.scss'
import StableBuildTargetTable from './components/StableBuildTargetTable'
import ReleaseAccordionList from './components/ReleaseAccordionList'

const { blackColor } = variables

const normalTextStyle = makeStyles(() => ({
  label: {
    '& .MuiFormLabel-root': {
      color: `${blackColor}`,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: `${blackColor}`,
    },
    '& .MuiFormLabel-root.Mui-disabled': {
      color: 'rgba(18, 18, 18, 0.54)',
    },
  },
}))

const helperTextStyles = makeStyles((theme) => ({
  root: {
    color: 'red !important',
  },
}))

const LOCAL_CONSTANT = {
  PRIVILEGE_ATTRIBUTE: {
    SHOP: 'SHOP',
    GROUP: 'GROUP',
  },
  PRIVILEGE_RESTURLID: 'privilegeRestUrlId',
  PRIVILEGE_GROUP_CODE: 'privilegeGroupCode',
}

export const AppVersionEdit = ({
  currentEditUser,
  setCurrentEditUser,
  hideTab,
  hideBottomBtnList,
  hidePrivilegeRestUrlIdEditButton = false,
  embeddedInRestaurantClone,
}) => {
  const dispatch = useDispatch()
  const store = useStore()
  const history = useHistory()
  const params = useParams()
  const helperTextStylesClasses = helperTextStyles()
  const normalTextStyleClasses = normalTextStyle()
  const [showDialog, setShowDialog] = useState(false)
  const [clear, setClear] = useState(true)
  const [statusDialog, setStatusDialog] = useState({ open: false, newStatus: null, id: null })
  const [previewSelectedList, setPreviewSelectedList] = useState([])
  const [restUrlIdList, setRestUrlIdList] = useState([])
  const [dialog, setDialog] = useState({
    open: false,
    dialogActions: [],
  })
  const [snackbar, setSnackbar] = useState({
      open: false,
      message: ''
  })
  const resource = 'appVersionPage'
  const savedAppVersion = useSelector((state) => state.appVersion.ui.appVersion)
  // console.log('savedAppVersion', savedAppVersion)
  const lang = useSelector((state) => state.lang.ui.lang)
  const theme = useSelector((state) => state.theme.ui.theme)
  const [inputError, setInputError] = useState({})
  // const [, setErrorVersion] = useState(AppVersionValidation.getVersion())
  const id = currentEditUser ? savedAppVersion.customId : savedAppVersion.buildVersion
  const iosOnly = savedAppVersion?.buildTarget && Object.keys(savedAppVersion?.buildTarget).every(key => key === 'ios' ? savedAppVersion.buildTarget[key] === true : savedAppVersion.buildTarget[key] === false )

  const releaseList = savedAppVersion.releaseList ?? []

  // Tabs: one per release
  const tabs = releaseList.map((release, idx) => ({
    label: { resource: resource, key: 'buildNumber' },
    // override with static label string if getString doesn't fit
    labelText: `#${release.buildNumber}${release.customCode ? ` · ${release.customCode}` : ''}`,
    tabIndex: idx,
  }))

  // Each release gets its own fields array
  const fieldsByRelease = useMemo(() => {
    return releaseList.map((release, idx) =>
      formFields({
        resource,
        savedAppVersion: release,
        appVersionId: savedAppVersion?.id,
        inputError: inputError[idx] ?? {},          // 👈 scope errors by index too
        helperTextStylesClasses,
        normalTextStyleClasses,
        theme,
        onChangeInput: (name, value) => {onChangeReleaseInput(idx, name, value)},
        handleSaveImage: (name, fileId) => onChangeReleaseInput(idx, name, fileId),
      })
    )
  }, [releaseList, inputError, theme])

  function onChangeReleaseInput(releaseIdx, nameOrPatch, value) {
    const updatedList = releaseList.map((r, idx) => {
      if (idx !== releaseIdx) return r
      // patch object: { releaseNote: '...', documentList: [...] }
      if (typeof nameOrPatch === 'object') return { ...r, ...nameOrPatch }
      // single field: ('releaseNote', '...')
      return { ...r, [nameOrPatch]: value }
    })
    dispatch(setAppVersionValue('releaseList', savedAppVersion, '', updatedList))
  }

  const content = [
    {
      tab: fieldsByRelease.map((inputSetting, index) => {
        let content
        switch (inputSetting.type) {
          case constants.TYPE.SELECT:
            content = <TempAutoSelectInput inputSetting={inputSetting} handleChange={onChangeInput} />
            break
          case constants.TYPE.CHECKBOX:
            content = (
              <span className="merchant-user-check-box-wrapper">
                <TempCheckBox
                  checkBoxSetting={{
                    className: inputSetting.className,
                    name: inputSetting.name,
                    label: inputSetting.label,
                    value: inputSetting.value,
                  }}
                  handleChange={handleCheckBoxChange}
                />
              </span>
            )
            break
          case constants.TYPE.FUNCTION:
            content = inputSetting.value
            break
          case constants.TYPE.TITLE:
            content = (
              <div className={`${inputSetting.className + ' sub-title'}`}>
                {getString(lang, inputSetting.value.resource, inputSetting.value.key, theme)}
              </div>
            )
            break
          case constants.TYPE.CUSTOM:
            content = inputSetting.customContent
            break
          default:
            content = <TempTextInput inputSetting={inputSetting} handleChange={inputSetting.handleChange} />
        }
        return (
          <div className="edit-display-wrapper" key={index}>
            {content}
            {inputSetting.isEndOfLine && <div></div>}
          </div>
        )
      }),
      btn: hideBottomBtnList ? false : true,
    },
  ]

  const bottomBtns = [
    {
      variant: constants.STYLE.VARIANT.CONTAINED,
      color: constants.STYLE.PRIMARY,
      label: { key: 'save' },
      onClick: onClickSave,
      className: 'margin-left-16',
      icon: <SaveIcon />,
    },
    // {
    //   variant: constants.STYLE.VARIANT.CONTAINED,
    //   color: constants.STYLE.PRIMARY,
    //   label: { key: 'clear' },
    //   disabled: false,
    //   onClick: onClickClear,
    //   className: 'margin-left-16',
    //   icon: <ClearIcon />,
    // },
    // {
    //   className: "margin-left-16",
    //   type: constants.TYPE.TEXT,
    //   label: { key: "lastLoginTimestamp" },
    //   value: savedSystemUser.lastLoginTimestamp ? ": " + moment(savedSystemUser.lastLoginTimestamp).format('YYYY-MM-DD HH:mm:ss') : " -----",
    // },
    {
      variant: constants.STYLE.VARIANT.CONTAINED,
      color: constants.STYLE.SECONDARY,
      label: { key: 'markAsPilot' },
      noRender: savedAppVersion.status !== "BETA" || !permissionsCheck(constants.PERMISSION_KEY.APPVERSION_APPROVAL),
      onClick: () => onClickUpdateStatus("PILOT"),
      className: 'special-button-margin-left',
      icon: <DoneIcon />,
    },
    {
      variant: constants.STYLE.VARIANT.CONTAINED,
      color: constants.STYLE.SUCCESS,
      label: { key: 'markAsStable' },
      noRender: savedAppVersion.status !== "PILOT" || !permissionsCheck(constants.PERMISSION_KEY.APPVERSION_APPROVAL),
      onClick: () => onClickUpdateStatus("STABLE"),
      className: 'special-button-margin-left',
       icon: <DoneIcon />,
    },
    {
      variant: constants.STYLE.VARIANT.CONTAINED,
      color: constants.STYLE.ERROR,
      label: { key: 'deprecate' },
      noRender: savedAppVersion.status == "DEPRECATED",
      onClick: () => onClickUpdateStatus("DEPRECATED"),
      className: 'special-button-margin-left',
       icon: <DeleteIcon />,
    },
    // {
    //   variant: constants.STYLE.VARIANT.CONTAINED,
    //   color: constants.STYLE.ERROR,
    //   label: { key: 'disable' },
    //   onClick: () => onClickUpdateStatus("DISABLED"),
    //   className: 'special-button-margin-left',
    //    icon: <ClearIcon />,
    // },
  ]

  const onClickBackToCreate = () => {
    setDialog({
      open: false,
      dialogActions: [],
    })
    onClickClear()
  }
  const onClickBackToList = () => {
    setDialog({
      open: false,
      dialogActions: [],
    })
    history.push('/' + constants.PATH.APPVERSION.APPVERSION)
  }

  function onChangeInput(name, e) {
    const isNumberField = ['buildNumber'].includes(name)
    const value = isNumberField ? Math.round(e) : e

    if (currentEditUser) {
      const temp = _.cloneDeep(currentEditUser)
      temp[`${name}`] = value
      setCurrentEditUser(temp)
    }

    dispatch(setAppVersionValue(name, savedAppVersion, '', value))
  }

  function handleSaveImage(name, fileId) {
    dispatch(setAppVersionValue(name, savedAppVersion, '', fileId))
  }

  function handleCheckBoxChange(name, e) {
    let tempAppVersion = _.cloneDeep(savedAppVersion)
    let permission = tempAppVersion[name]
    if (!e) {
      permission['read'] = true
      permission['write'] = true
    } else {
      permission['read'] = false
      permission['write'] = false
    }
    dispatch(setAppVersionValue(name, savedAppVersion, '', permission))
  }

  async function onClickSave() {
    // validate all releases
    const errors = releaseList.reduce((acc, release, idx) => {
      const releaseErrors = validateForm(release)   // validation fn
      if (Object.keys(releaseErrors).length) acc[idx] = releaseErrors
      return acc
    }, {})

    if (Object.keys(errors).length) {
      setInputError(errors)
      return
    }

    setShowDialog(true)

    // submit savedAppVersion which contains the full updated releaseList
    // await dispatch(putAppVersion(savedAppVersion))
  }

  async function onClickConfirmSave() {
    setShowDialog(false)
    
    // if (savedAppVersion.status == "BETA" && !permissionsCheck(constants.PERMISSION_KEY.APPVERSION_APPROVAL)) {
        const body = {
          // ...savedAppVersion,
          // file: iosOnly ? null : savedAppVersion.file, // if it's ios only, set file to null to avoid validation error on backend
          releaseList: savedAppVersion.releaseList,
        }

        newPut({
          path: 'gulu-console/merchant/shop-app-release' + "/" + params.id,
          body: body,
          successCallback: (payload) => {
            setDialog({
              open: true,
              onClose: onClickBackToCreate,
              dialogContentText: { key: 'editSuccess' },
              dialogActions: [
                { label: { key: 'backEdit' }, onClick: onClickBackToCreate, icon: <ArrowBackIcon /> },
                { label: { key: 'backList' }, onClick: onClickBackToList, icon: <LowPriorityIcon /> },
              ],
            })
            setClear(false)
          },
          store: store,
        })
    //   } else {
    //     const body = {
    //       documentList: savedAppVersion.documentList,
    //       releaseNote: savedAppVersion.releaseNote,
    //       remarks: savedAppVersion.remarks,
    //     }
    //     newPatch({
    //       path: 'gulu-console/merchant/shop-app-release' + "/" + params.id,
    //       body: body,
    //       successCallback: (payload) => {
    //         setDialog({
    //           open: true,
    //           onClose: onClickBackToCreate,
    //           dialogContentText: { key: 'editSuccess' },
    //           dialogActions: [
    //             { label: { key: 'backEdit' }, onClick: onClickBackToCreate, icon: <ArrowBackIcon /> },
    //             { label: { key: 'backList' }, onClick: onClickBackToList, icon: <LowPriorityIcon /> },
    //           ],
    //         })
    //         setClear(false)
    //       },
    //       store: store,
    //     })
    //   }
  }

  function onClickUpdateStatus(newStatus, id) {
    // if (newStatus === 'STABLE' && releaseList.some(r => r.status === 'STABLE')) {
    //   setSnackbar({
    //     open: true,
    //     // message: getString(lang, resource, 'stableStatusExists', theme),
    //     message: 'Only one release can be marked as Stable. Please deprecate the existing Stable release before marking this release as Stable.',
    //   })
    //   return
    // }
    setStatusDialog({ open: true, newStatus, id })
  }

  async function confirmUpdateStatus() {
    const newStatus = statusDialog.newStatus
    setStatusDialog({ open: false, newStatus: null })
    const body = {
      status: newStatus,
    }
    newPut({
        // path: 'gulu-console/merchant/shop-app-release-status' + "/" + params.id,
        path: 'gulu-console/merchant/shop-app-release-status' + "/" + statusDialog.id,
        body: body,
        successCallback: (payload) => {
          setDialog({
            open: true,
            onClose: onClickBackToCreate,
            dialogContentText: { key: 'editSuccess' },
            dialogActions: [
              { label: { key: 'backEdit' }, onClick: onClickBackToCreate, icon: <ArrowBackIcon /> },
              { label: { key: 'backList' }, onClick: onClickBackToList, icon: <LowPriorityIcon /> },
            ],
          })
          setClear(false)
        },
        store: store,
      })
  }

  function onClickClear() {
    setClear(true)
  }

  useEffect(() => {
    // if (!currentEditUser && !embeddedInRestaurantClone) {
    //   dispatch(setAppVersion(constants.APP_VERSION_INPUT))
    //   setClear(false)
    //   // AppVersionValidation.resetErrorMessages()
    //   setInputError({})
    // }
    if (clear === true) {
      const newListPagination = {
        // ...listPagination,
        // ...filter,
        // pageSize: tempParam.rowsPerPage,
        // page: tempParam.page,
        sortingList: [
            { sort: 'createDateTime', direction: 'DESC' }
        ]
      }

      getOne('gulu-console/merchant/shop-app-release' + "/" + params.id, newListPagination, (payload) => {
        // payload.scNameDisable = getScDisabled(payload.tcName, payload.scName);
        dispatch(setAppVersion(_.cloneDeep(payload)));
        // setAreaInputList(_.cloneDeep(payload))
        // setReady(true)
        setClear(false)
      }, undefined, store);

      // AppVersionValidation.resetErrorMessages()
      setInputError({})
    }
    
    // dispatch(setAppVersion(dummyPayload))
  }, [clear === true]) // eslint-disable-line react-hooks/exhaustive-deps

  const renderField = (inputSetting, index) => {

    // full-width types
    const isFullWidth = [
      constants.TYPE.TITLE,
      // constants.TYPE.FUNCTION,
      // constants.TYPE.CUSTOM,
    ].includes(inputSetting.type)

    let fieldContent
    switch (inputSetting.type) {
      case constants.TYPE.SELECT:
        fieldContent = <TempAutoSelectInput inputSetting={inputSetting} handleChange={inputSetting.handleChange ?? onChangeInput} />
        break
      case constants.TYPE.CHECKBOX:
        fieldContent = (
          <TempCheckBox
            checkBoxSetting={{
              className: inputSetting.className,
              name: inputSetting.name,
              label: inputSetting.label,
              value: inputSetting.value,
            }}
            handleChange={handleCheckBoxChange}
          />
        )
        break
      case constants.TYPE.FUNCTION:
        fieldContent = inputSetting.value
        break
      case constants.TYPE.TITLE:
        fieldContent = (
          <Typography variant="subtitle2" className="sub-title" sx={{ mt: 1, mb: 0.5, width: '100%' }}>
            {getString(lang, inputSetting.value.resource, inputSetting.value.key, theme)}
          </Typography>
        )
        break
      case constants.TYPE.CUSTOM:
        fieldContent = inputSetting.customContent
        break
      default:
        fieldContent = <TempTextInput inputSetting={inputSetting} handleChange={inputSetting.handleChange ?? onChangeInput} />
    }

    return (
      <Grid
        item
        key={index}
        xs={12}
        sm={isFullWidth || inputSetting.fullWidth ? 12 : 6}
        md={isFullWidth || inputSetting.fullWidth ? 12 : inputSetting.md ?? 4}
        sx={inputSetting.isEndOfLine ? { flexBasis: '100%', maxWidth: '100%' } : {}}
      >
        {fieldContent}
      </Grid>
    )
  }

  const DEFAULT_RELEASE = {
    id: null,
    appVersion: savedAppVersion.appVersion,
    buildNumber: null,
    customCode: null,
    tagList: [],
    buildTarget: {
      androidModern: false,
      androidLegacy: false,
      androidMpos: false,
      ios: false,
    },
    releaseNote: '',
    remarks: '',
    documentList: [],
    status: 'BETA',
    file: { cosFileKey: null, cosFileUrl: '' },
  }

  function onAddRelease() {
    const updatedList = [...releaseList, { ...DEFAULT_RELEASE }]
    dispatch(setAppVersionValue('releaseList', savedAppVersion, '', updatedList))
  }

  function onRemoveRelease(idx) {
    const updatedList = releaseList.filter((_, i) => i !== idx)
    dispatch(setAppVersionValue('releaseList', savedAppVersion, '', updatedList))
  }

  // Group fields by tabIndex (default to 0 if not specified)
  const fieldsByTab = useMemo(() => {
    return fieldsByRelease.reduce((acc, field, index) => {
      const tabIdx = field.tabIndex ?? 0
      if (!acc[tabIdx]) acc[tabIdx] = []
      acc[tabIdx].push({ field, index })
      return acc
    }, {})
  }, [fieldsByRelease])

  // Build a map: buildTarget key → first STABLE release that has it enabled
  const stableBuildTargetMap = useMemo(() => {
    const buildTargetKeys = ['androidModern', 'androidLegacy', 'androidMpos', 'ios']
    return buildTargetKeys.reduce((acc, key) => {
      const firstStable = releaseList.find(
        (r) => r.status === 'STABLE' && r.buildTarget?.[key] === true
      )
      if (firstStable) { 
        acc[key] = firstStable 
      } else {
        acc[key] = null
      }
      return acc
    }, {})
  }, [releaseList])

  return (
    <Box>
      <EditCreateDisplayV2>
        <EditCreateDisplayV2.Header
          resource={resource}
          display={savedAppVersion.id ? 'edit' : 'create'}
          code={`v${savedAppVersion?.id ?? ''} ${releaseList[1]?.customCode ?? ``}`}
        />

        <EditCreateDisplayV2.Body>
          <StableBuildTargetTable stableBuildTargetMap={stableBuildTargetMap} />

          <ReleaseAccordionList
            releaseList={releaseList}
            fieldsByRelease={fieldsByRelease}
            inputError={inputError}
            hideBottomBtnList={hideBottomBtnList}
            lang={lang}
            theme={theme}
            resource={resource}
            renderField={renderField}
            onAddRelease={onAddRelease}
            onRemoveRelease={onRemoveRelease}
            onClickUpdateStatus={onClickUpdateStatus}
            onChangeRelease={onChangeReleaseInput}
          />

        </EditCreateDisplayV2.Body>

        {/* Single footer for whole form */}
        {!hideBottomBtnList && (
          <EditCreateDisplayV2.Footer>
            <Button variant="contained" color="primary" startIcon={<SaveIcon />} onClick={onClickSave}>
              {getString(lang, '', 'save', theme)}
            </Button>
            {/* {savedAppVersion.status !== 'DEPRECATED' && (
              <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={() => onClickUpdateStatus('DEPRECATED')}>
                {getString(lang, resource, 'deprecate', theme)}
              </Button>
            )} */}
          </EditCreateDisplayV2.Footer>
        )}
      </EditCreateDisplayV2>

      <SharedDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        title={'Confirm'}
        content={
          <>
            <Typography variant='body1'>Are you sure you want to save the changes?</Typography>
            {
              savedAppVersion?.buildTarget && iosOnly  && (
                <Typography variant='body2' color="textSecondary" sx={{ mt: 2 }}>
                  Note: Changes to iOS versions will auto delete the existing APK file as iOS versions do not require an APK. You will need to re-upload the APK if you switch back to Android build target.
                </Typography>
              )
            }
          </>
        }
        actions={
          <Stack direction={'row'} gap={1}>
            <Button variant='outlined' onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button variant='contained' onClick={() => onClickConfirmSave()}>Save</Button>
          </Stack>
        }
      />
      <SharedDialog
        open={statusDialog.open}
        onClose={() => setStatusDialog({ open: false, newStatus: null })}
        title={'Confirm'}
        content={<Typography>Are you sure you want to change status to {statusDialog?.newStatus && <StatusLabel resource={resource} keyName={statusDialog?.newStatus} />}?</Typography>}
        actions={
          <Stack direction={'row'} gap={1}>
            <Button variant='outlined' onClick={() => setStatusDialog({ open: false, newStatus: null })}>Cancel</Button>
            <Button variant='contained' onClick={() => confirmUpdateStatus()}>Confirm</Button>
          </Stack>
        }
      />
      {dialog && dialog.open && <GlobalDialog dialogInfo={dialog} />}
      <Snackbar
          open={snackbar.open}
          // message={snackbar.message}
          autoHideDuration={10000}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transitionDuration={0}
          onClose={() => setSnackbar({ open: false, message: '' })}
      >
        <Alert
          onClose={() => setSnackbar({ open: false, message: '' })}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}